import { appOrigin } from '@easydent/shared-ui';

/**
 * Which guide answers the page you are on.
 *
 * One map, consulted by every help affordance, rather than a link hand-placed on each page. Pages
 * get created without anyone remembering to add help; a central map makes the omission visible in
 * one file instead of invisible across forty.
 *
 * Order matters: the first pattern that matches wins, so put specific routes above the general ones
 * they sit under (`/patients/duplicates` before `/patients/:id`).
 *
 * ⚠️ Every slug here must exist in `docs-app/src/content/en/`. `check-help-links.mjs` enforces it in
 * CI — a renamed guide would otherwise turn a working link into a 404 that nobody notices until a
 * clinic reports it.
 */
export const HELP_LINKS: { pattern: RegExp; slug: string }[] = [
  // Clinical and front desk
  { pattern: /^\/calendar/, slug: 'book-an-appointment' },
  { pattern: /^\/consultations\//, slug: 'run-a-consultation' },
  { pattern: /^\/patients\/duplicates/, slug: 'data-and-gdpr' },
  { pattern: /^\/patients\/[^/]+/, slug: 'patient-record' },
  { pattern: /^\/patients/, slug: 'add-a-patient' },
  { pattern: /^\/dashboard/, slug: 'dashboard' },
  { pattern: /^\/$/, slug: 'dashboard' },

  // Your own working life
  { pattern: /^\/my-schedule/, slug: 'my-schedule' },
  { pattern: /^\/profile/, slug: 'profile-and-security' },

  // Running the practice
  { pattern: /^\/team-schedule/, slug: 'schedules-and-approvals' },
  { pattern: /^\/schedule/, slug: 'schedules-and-approvals' },
  { pattern: /^\/approvals/, slug: 'schedules-and-approvals' },
  { pattern: /^\/staff/, slug: 'staff-and-roles' },
  { pattern: /^\/treatments/, slug: 'treatments-and-pricing' },
  { pattern: /^\/inventory/, slug: 'inventory' },
  { pattern: /^\/sterilization/, slug: 'inventory' },
  { pattern: /^\/supplier-invoices/, slug: 'supplier-invoices' },
  { pattern: /^\/partner-accounts/, slug: 'imaging-and-files' },

  // Money, growth, oversight
  { pattern: /^\/reports/, slug: 'reports' },
  { pattern: /^\/campaigns/, slug: 'campaigns' },
  { pattern: /^\/audit/, slug: 'audit-log' },

  // Help about help
  { pattern: /^\/support/, slug: 'get-help' },
];

/**
 * Settings is one route with nine sections behind `?section=`, so matching the path alone would
 * send everyone to the same guide regardless of what they are configuring — which is the case where
 * contextual help is worth the most.
 */
export const SETTINGS_HELP: Record<string, string> = {
  locations: 'locations-and-chairs',
  imaging: 'imaging-and-files',
  notifications: 'messages-and-reminders',
  ai: 'run-a-consultation',
  billing: 'plan-and-billing',
  listing: 'public-listing',
  developer: 'api-access',
  data: 'data-and-gdpr',
  fiscal: 'e-factura',
};

/**
 * The guide that explains how to fix each guidance finding.
 *
 * The dashboard already tells a clinic what is wrong — an unstaffed chair, an empty catalogue. What
 * it could not do is say how to put it right, which is the moment help is worth most: the reader is
 * not browsing, they are stuck on a specific thing. A finding with no entry here simply shows no
 * link, rather than guessing.
 */
export const GUIDANCE_HELP: Record<string, string> = {
  'setup.seatUnstaffed': 'staff-and-roles',
  'setup.doctorNoHours': 'schedules-and-approvals',
  'setup.catalogEmpty': 'treatments-and-pricing',
  'setup.noOtherStaff': 'staff-and-roles',
  'setup.cabinetNoAddress': 'locations-and-chairs',
  'setup.cabinetNoTaxId': 'e-factura',
  'setup.listingUnpublished': 'public-listing',
  'setup.paymentsNotConnected': 'plan-and-billing',
  'setup.walletNoThreshold': 'plan-and-billing',
  'setup.localisationMismatch': 'locations-and-chairs',
};

/** The guide for a guidance finding, or null when none is a good enough answer. */
export const helpSlugForGuidance = (key: string): string | null => GUIDANCE_HELP[key] ?? null;

/** The guide for a location, or `null` when the index is the honest answer. */
export function helpSlugForPath(pathname: string, search = ''): string | null {
  if (/^\/settings/.test(pathname)) {
    const section = new URLSearchParams(search).get('section');
    // No section yet means the reader has just arrived and has not chosen; the locations guide is
    // where a new clinic needs to start, and it is the first tab.
    return SETTINGS_HELP[section ?? ''] ?? 'locations-and-chairs';
  }
  return HELP_LINKS.find((l) => l.pattern.test(pathname))?.slug ?? null;
}

/**
 * The one place the guides live.
 *
 * <p>This used to derive a per-environment host by swapping the first DNS label
 * (`clinic.<env>` → `docs.<env>`). That was clever and wrong: the
 * guides are static, public and version-agnostic, so "how to book an appointment" is the same
 * sentence whichever environment the reader came from. Deriving a host per environment only ever
 * produced a second copy to keep current, and a second hostname to keep alive.</p>
 *
 * <p>The host comes from `appOrigin('docs')` in shared-ui, which is the single place any app asks
 * where another app lives — and the only place a URL appears at all.</p>
 *
 * <p>⚠️ One consequence worth knowing: on staging this app is served from `easysoft.ro` while the
 * guides are on `easydent365.com`, so the shared `edlang` cookie — which is scoped to a parent
 * domain — cannot span the two. The `?lang=` parameter below is therefore no longer a belt-and-
 * braces extra for first visits; it is the ONLY thing carrying the reader's language across. Do not
 * remove it on the grounds that the cookie already covers it.</p>
 */
export function docsUrl(slug?: string | null, lang?: string): string {
  const url = new URL(`${appOrigin('docs')}/${slug ?? ''}`);
  const { href } = window.location;
  // `return` brings the reader back to the screen they left rather than a generic home page.
  url.searchParams.set('return', href);
  // Carry the language explicitly: the shared cookie covers it, but a first visit from a browser
  // set to another language would otherwise open in the wrong one.
  if (lang) url.searchParams.set('lang', lang.slice(0, 2));
  return url.toString();
}
